import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	OnDestroy,
	OnInit,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import {
	finalize,
	map,
	takeUntil,
} from 'rxjs/operators';

import {
	Observable,
	Subject,
	of,
} from 'rxjs';

import { CustomFormControlWithField, CustomFormGroup as CustomFormGroupNew } from '@rs-bank/rs-forms-extention';

import { IbInputField } from 'ib/internetbank-fields/field-types/input.ib.field-types';
import { IbSelectField } from 'ib/internetbank-fields/field-types/select.ib.field-types';


import { InternetbankAppApplicationException } from '../../../../../common/exceptions/exceptions';
import { SelectItem } from '../../../../../ui/controls/select-field/select-item';
import { SubscriptionEducationUtils, SubscriptionEducationUtilsOptions } from '../../../../settings/tabs/settings-tab-3/subscriptions-education-list-fields-item/subscription-education-utils';
import { PaymentWidgetData } from '../payment-widget-data';
import { DocumentTypes, PaymentWidgetsServiceService } from '../payment-widgets.service';
import { ChargeForWidget } from '../shared/charge-item/change-item-entites';
import { WidgetBaseDirective } from '../shared/widget-base-component';

@Component({
	selector: 'app-widget-education',
	templateUrl: './widget-education.component.html',
	styleUrls: ['./widget-education.component.less'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class WidgetEducationComponent extends WidgetBaseDirective implements OnInit, OnDestroy {

	private typeSelectList: Array<SelectItem<DocumentTypes>> = [
		new SelectItem(
			SubscriptionEducationUtils.documentNames[DocumentTypes.UIN],
			DocumentTypes.UIN,
		),
		new SelectItem(
			SubscriptionEducationUtils.documentNames[DocumentTypes.SNILS],
			DocumentTypes.SNILS,
		),
		new SelectItem(
			SubscriptionEducationUtils.documentNames[DocumentTypes.BIRTH_CERTIFICATE],
			DocumentTypes.BIRTH_CERTIFICATE,
		),
	];

	private ngUnsubscribe: Subject<void> = new Subject();

	constructor(
		protected override router: Router,
		protected override paymentWidgetsServiceService: PaymentWidgetsServiceService,
		protected override changeDetectorRef: ChangeDetectorRef,
	) {
		super(router, paymentWidgetsServiceService, changeDetectorRef);
	}

	public ngOnInit(): void {
		this.searchFormNew = new CustomFormGroupNew({
			typeSelect: new CustomFormControlWithField(
				new IbSelectField<SelectItem<DocumentTypes>>({
					options$: of(this.typeSelectList),
					displayFunction: (option: SelectItem<DocumentTypes>) => option.label,
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					getValueFunction: (option: SelectItem<DocumentTypes>) => option.value as any,
				}),
			),
			searchText: new CustomFormControlWithField(
				new IbInputField({ inputMode: 'numeric' }),
				null,
				Validators.required,
			),
			birthCertificate: new FormControl(
				null,
				Validators.required,
			),

		});

		const typeSelectControl: CustomFormControlWithField | null = this.searchFormNew.getControlFromAllLevels('typeSelect');
		const searchTextControl: CustomFormControlWithField | null = this.searchFormNew.getControlFromAllLevels('searchText');
		const searchTextUIComponent: IbInputField | undefined = searchTextControl?.fieldContext;

		typeSelectControl?.valueChanges
			.pipe(
				takeUntil(this.ngUnsubscribe),
			)
			.subscribe((value: DocumentTypes) => {
				const options: SubscriptionEducationUtilsOptions = SubscriptionEducationUtils.getOptionsByType(value);

				this.chargeWidget.searcErrors = null;
				this.charges = [];
				searchTextControl?.reset();

				if (searchTextUIComponent) {
					searchTextUIComponent.hints = { underLine: options.hints.underLineHint };
					searchTextUIComponent.inputMask = options.inputMask;
				}

				searchTextControl?.updateValueAndValidity({ emitEvent: false });

				this.changeDetectorRef.markForCheck();
			});

		searchTextControl?.valueChanges
			.pipe(
				takeUntil(this.ngUnsubscribe),
			)
			.subscribe(() => {
				searchTextControl.markAsDirty();
			});

		if (typeSelectControl?.fieldContext.getFieldComponentRef) {
			typeSelectControl.fieldContext.getFieldComponentRef()
				?.fieldComponentAfterViewInit$
				.pipe(
					takeUntil(this.ngUnsubscribe),
				)
				.subscribe(() => {
					typeSelectControl.setValue(this.typeSelectList[0].value);
				});
		}

		this.onTabSubscriptionActivate();
	}

	public ngOnDestroy(): void {
		this.ngUnsubscribe.next();
		this.ngUnsubscribe.complete();
	}

	public onTabSubscriptionActivate(): void {
		this.paymentWidgetsServiceService
			.getEducationOnSubscription()
			.pipe(
				takeUntil(this.ngUnsubscribe),
				finalize(() => {
					this.changeDetectorRef.markForCheck();
				}),
			)
			.subscribe((data: PaymentWidgetData) => {
				this.charges = data.accruals;
				this.subscriptionsAbsence = data.subscriptionsAbsence;
			});
	}

	public onTabSearchActivate(): void { }

	public search(): void {
		this.chargeWidget.searcErrors = null;

		const typeSelectControl: CustomFormControlWithField | null = this.searchFormNew.getControlFromAllLevels('typeSelect');
		const searchTextControl: CustomFormControlWithField | null = this.searchFormNew.getControlFromAllLevels('searchText');
		const birthCertificate: CustomFormControlWithField | null = this.searchFormNew.getControlFromAllLevels('birthCertificate');

		this.chargeWidget.startSearch();

		let search$: Observable<Array<ChargeForWidget>> | undefined;

		switch (typeSelectControl?.value) {
			case DocumentTypes.UIN:
				search$ = this.paymentWidgetsServiceService
					.searchOnUin(searchTextControl?.value)
					.pipe(map((data: ChargeForWidget) =>
						data ? [data] : []));
				break;
			case DocumentTypes.SNILS:
			case DocumentTypes.PASSPORT:
				search$ = this.paymentWidgetsServiceService
					.searchOnDocument(typeSelectControl.value, searchTextControl?.value);
				break;

			case DocumentTypes.BIRTH_CERTIFICATE:
				search$ = this.paymentWidgetsServiceService
					.searchOnDocument(typeSelectControl.value, birthCertificate?.value);
				break;
			default:
				break;
		}
		if (search$) {
			search$
				.pipe(
					takeUntil(this.ngUnsubscribe),
					finalize(() => {
						this.chargeWidget.stopSearch();
						this.changeDetectorRef.markForCheck();
					}),
				)
				.subscribe(
					(data: Array<ChargeForWidget>) => {
						this.charges = data;
						this.chargeWidget.startSearch();
					},
					(error: InternetbankAppApplicationException) => {
						this.chargeWidget.searcErrors = error.errors || null;
					});
		}

	}

}
